
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const ResetPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage(null);
    
    try {
      console.log("[ResetPassword] Sending password reset for:", email);
      
      // Include the full redirect URL
      const redirectTo = `${window.location.origin}/update-password`;
      console.log("[ResetPassword] Redirect URL:", redirectTo);
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectTo,
      });
      
      if (error) {
        console.error("[ResetPassword] Reset error:", error);
        setErrorMessage(error.message);
        toast.error("Reset failed: " + error.message);
      } else {
        console.log("[ResetPassword] Reset email sent successfully");
        setSuccess(true);
        toast.success("Password reset instructions sent to your email!");
      }
    } catch (error: any) {
      console.error("[ResetPassword] Reset exception:", error);
      setErrorMessage(error.message || "Something went wrong. Please try again.");
      toast.error("Failed to send reset instructions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-12 flex flex-col items-center">
      <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-xl shadow-lg border border-heaven-gold/20">
        <div className="text-center">
          <h1 className="text-3xl font-playfair font-bold text-gray-800">Reset Password</h1>
          <p className="mt-2 text-gray-600">Enter your email to receive reset instructions</p>
        </div>

        {errorMessage && (
          <Alert variant="destructive" className="border-red-500 text-red-700 bg-red-50">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        {success ? (
          <div className="space-y-4 text-center">
            <p className="text-green-600">Check your email for password reset instructions.</p>
            <Button
              variant="link"
              className="text-heaven-gold hover:text-heaven-dark-gold"
              onClick={() => navigate("/sign-in")}
            >
              Return to Sign In
            </Button>
          </div>
        ) : (
          <form onSubmit={handleResetPassword} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <Button
              type="submit"
              variant="gold"
              className="w-full py-6"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending Instructions...
                </>
              ) : (
                "Send Reset Instructions"
              )}
            </Button>

            <div className="text-center">
              <Button
                variant="link"
                className="text-heaven-gold hover:text-heaven-dark-gold"
                onClick={() => navigate("/sign-in")}
              >
                Back to Sign In
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
